/** Spacecraft sprites: constant on-screen size (≥ the profile's minimum), agency colour, icon per type. */
import * as THREE from 'three';
import { placeRelative } from './floatingOrigin';
import { MODEL_SIZE_M, createVoyagerModel } from './spacecraftModel';
import { endMarkerTexture, iconTypeOf, spacecraftIconTexture } from './textures';

const SPACECRAFT_HDR = 3;
import type { World, WorldSpacecraft } from './world';
import { moonBlur } from '../core/moons';
import { symbolicOrbitOffset } from '../core/symbolicOrbit';
import { vec3 } from '../core/vec3';

export interface SpacecraftVisual {
  sc: WorldSpacecraft;
  sprite: THREE.Sprite;
  /** Where the trajectory ended (D11): stays after the spacecraft is gone. */
  endMarker: THREE.Sprite;
  /** Tier B: the symbolic orbit ring around the Earth (S3-21), positions relative to the Earth's display position. */
  ring?: { line: THREE.LineLoop; positions: Float32Array };
  icon: THREE.Texture;
  silentIcon: THREE.Texture;
  /** Close-up 3D model (S2-15), shown instead of the icon while the spacecraft is focused or followed. */
  model?: THREE.Group;
}

/** On-screen size of the close-up model, px (phones: smaller, the planet should stay the main subject). */
const MODEL_PX = 70;
const MODEL_PX_NARROW = 48;
/** Samples of a symbolic orbit ring. */
const RING_SAMPLES = 96;

export class SpacecraftSprites {
  readonly root = new THREE.Group();
  readonly visuals: SpacecraftVisual[] = [];
  private readonly target = new THREE.Vector3();
  private readonly baseColors = new Map<THREE.SpriteMaterial, THREE.Color>();

  constructor(private readonly world: World) {
    const icons = new Map<string, THREE.Texture>();
    const iconFor = (type: string, silent: boolean): THREE.Texture => {
      const key = `${iconTypeOf(type)}:${silent}`;
      let tex = icons.get(key);
      if (!tex) icons.set(key, (tex = spacecraftIconTexture(iconTypeOf(type), silent)));
      return tex;
    };
    const endTex = endMarkerTexture();
    for (const sc of world.spacecraft) {
      const icon = iconFor(sc.type, false);
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: icon,
          color: new THREE.Color(sc.color),
          sizeAttenuation: false,
          transparent: true,
          depthWrite: false,
          depthTest: false,
        }),
      );
      this.baseColors.set(sprite.material, sprite.material.color.clone());
      sprite.renderOrder = 10;
      sprite.name = sc.id;
      const endMarker = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: endTex,
          color: new THREE.Color(sc.color),
          sizeAttenuation: false,
          transparent: true,
          opacity: 0.8,
          depthWrite: false,
          depthTest: false,
        }),
      );
      endMarker.renderOrder = 10;
      endMarker.name = `${sc.id}:end`;
      endMarker.visible = false;
      this.root.add(sprite, endMarker);
      const visual: SpacecraftVisual = { sc, sprite, endMarker, icon, silentIcon: iconFor(sc.type, true) };
      if (sc.symbolic) {
        const positions = new Float32Array(3 * RING_SAMPLES);
        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage));
        const line = new THREE.LineLoop(
          geom,
          new THREE.LineBasicMaterial({ color: sc.color, transparent: true, opacity: 0.55, depthWrite: false }),
        );
        line.frustumCulled = false;
        line.visible = false;
        this.root.add(line);
        visual.ring = { line, positions };
      }
      // the model only for the Voyagers (D18: a wrong model is worse than none)
      if (/^voyager/.test(sc.id)) {
        visual.model = createVoyagerModel();
        visual.model.visible = false;
        this.root.add(visual.model);
      }
      this.visuals.push(visual);
    }
  }

  private speedDaysPerS = 0;
  private readonly ringP = vec3();
  private readonly ringD = vec3();

  /** Mission days per real second of the current playback: fast orbits blur into their ring (as moons do). */
  setPlaybackSpeed(daysPerSecond: number): void {
    this.speedDaysPerS = daysPerSecond;
  }

  /** HDR on/off (S2-07): spacecraft glow slightly with post-processing (gain > 1 crosses the bloom threshold). */
  setHdr(on: boolean): void {
    for (const [material, base] of this.baseColors) material.color.copy(base).multiplyScalar(on ? SPACECRAFT_HDR : 1);
  }

  /**
   * @param closeId the spacecraft shown as a 3D model (focused or followed), or null
   * @param earth Earth's display position (du): the high-gain antenna points there
   */
  update(
    origin: { x: number; y: number; z: number },
    sizePx: number,
    pxPerDuAtUnit: number,
    closeId: string | null = null,
    camera: THREE.Camera | null = null,
    earth: { x: number; y: number; z: number } | null = null,
    narrow = false,
  ): void {
    const s = sizePx / pxPerDuAtUnit;
    for (const v of this.visuals) {
      if (v.ring) this.updateRing(v, origin);
      v.sprite.visible = v.sc.visible;
      v.endMarker.visible = v.sc.endVisible;
      if (v.sc.endVisible) {
        placeRelative(v.endMarker, v.sc.end, origin);
        v.endMarker.scale.set(s * 0.8, s * 0.8, 1);
      }
      if (!v.sc.visible) {
        if (v.model) v.model.visible = false;
        continue;
      }
      placeRelative(v.sprite, v.sc.d, origin);
      v.sprite.scale.set(s, s, 1);
      // silent (contact lost, D11): the hollow icon at half strength
      const silent = v.sc.lifeState === 'silent';
      const map = silent ? v.silentIcon : v.icon;
      if (v.sprite.material.map !== map) v.sprite.material.map = map;
      const close = !!v.model && v.sc.id === closeId && !!camera;
      // the icon stays (it carries the label) but becomes invisible while the model is shown
      // a 90-minute orbit at 1 day/s would jump around the Earth: the icon fades and the ring stays (like the moons)
      const blur = v.sc.symbolic ? moonBlur(v.sc.symbolic.period / 1440, this.speedDaysPerS) : 0;
      v.sprite.material.opacity = (close ? 0 : silent ? 0.5 : 1) * (1 - blur);
      if (!v.model) continue;
      v.model.visible = close;
      if (!close) continue;
      v.model.position.copy(v.sprite.position);
      const dist = v.model.position.distanceTo(camera!.position);
      const px = narrow ? MODEL_PX_NARROW : MODEL_PX;
      v.model.scale.setScalar(((px / pxPerDuAtUnit) * dist) / MODEL_SIZE_M);
      if (earth) {
        this.target.set(earth.x - origin.x, earth.y - origin.y, earth.z - origin.z);
        v.model.lookAt(this.target);
      }
    }
  }

  /** Maps a Tier B spacecraft's symbolic orbit (one period) through the Earth's bubble, as it is now. */
  private updateRing(v: SpacecraftVisual, origin: { x: number; y: number; z: number }): void {
    const ring = v.ring!;
    ring.line.visible = v.sc.visible;
    if (!v.sc.visible) return;
    const earth = this.world.bodyById.get('earth')!;
    const o = v.sc.symbolic!;
    const p = this.ringP;
    const d = this.ringD;
    for (let k = 0; k < RING_SAMPLES; k++) {
      symbolicOrbitOffset(o, earth.mapped.radius, ((k / RING_SAMPLES) * o.period) / 1440, p);
      this.world.mapping.mapRelative(earth.mapped, earth.p, p, d);
      ring.positions[3 * k] = d.x - earth.d.x;
      ring.positions[3 * k + 1] = d.y - earth.d.y;
      ring.positions[3 * k + 2] = d.z - earth.d.z;
    }
    (ring.line.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    placeRelative(ring.line, earth.d, origin);
  }
}
